import {
  useContext, useCallback, useState, useMemo
} from "react";
import { useHistory, useParams } from "react-router-dom";
import clsx from "clsx";
import type { BusinessSubscription, LensSDKException } from "lens-platform-sdk";
import { ButtonBar } from "@k8slens/lds";

import Layout from "src/components/Layout/Layout";
import { useLogout } from "src/hooks/useLogout";
import { ProfileContext } from "src/providers/profile-provider";
import { Administrators } from "src/components/Business/Administrators";
import { useGetBusiness } from "src/hooks/useGetBusiness";
import { useAnalytics } from "src/hooks/useAnalytics";
import { useCreateBusinessInvitation } from "src/hooks/useCreateBusinessInvitation";
import { useGetBusinessUsers } from "src/hooks/useGetBusinessUsers";
import { getUniqueEmailsArray } from "src/services/email";
import TrackedButton from "src/components/Button/TrackedButton";
import InviteAdminModal from "src/components/Business/InviteAdminModal";
import Notification from "src/components/Notification/Notification";
import { BusinessAdminInvitation, useGetBusinessInvitations } from "src/hooks/useGetBusinessInvitations";
import { useGetBusinessSubscriptions } from "src/hooks/useGetBusinessSubscriptions";
import { useChangeBusinessUserRole } from "src/hooks/useChangeBusinessUserRole";
import { useDeleteBusinessInvitation } from "src/hooks/useDeleteBusinessInvitation";
import { useDeleteBusinessUser } from "src/hooks/useDeleteBusinessUser";
import styles from "src/components/Business/Account.module.css";
import { useBusinessParent } from "src/hooks/useBusinessParent";
import { isNotProduction } from "src/constants";
import { Children } from "./Children";
import { Parent } from "./Parent";

const inviteAdminLabel = "Invite Administrator";

const getUserSubscriptions = (businessSubscriptions: BusinessSubscription[], userId: string) => {
  const userSubscriptions = businessSubscriptions
    .filter((subscription) => {
      const userSeats = subscription.usedSeats
        .filter((seat) => seat.user?.id === userId);

      return userSeats.length > 0;
    });

  return userSubscriptions;
};

const Account = () => {
  const logout = useLogout();
  const { profile } = useContext(ProfileContext);
  const { businessId } = useParams<{ businessId?: string }>();
  const history = useHistory();
  const { business } = useGetBusiness(businessId);
  const { businessUsers: businessAdministrators, reload: reloadBusinessUsers, loading: loadingBusinessUsers } = useGetBusinessUsers(businessId, "Administrator");
  const activeBusinessAdministrators = useMemo(() => businessAdministrators.filter((user) => user.state === "active"), [businessAdministrators]);
  const { businessInvitations, reload: reloadBusinessInvitations } = useGetBusinessInvitations(businessId, "pending");
  const businessAdminInvitations = useMemo(() => businessInvitations.filter(
    (invitation): invitation is BusinessAdminInvitation => invitation.role === "Administrator"
  ), [businessInvitations]);
  const { businessSubscriptions } = useGetBusinessSubscriptions(businessId);
  const { track, trackError } = useAnalytics();
  const [inviteAdminModalOpen, setInviteAdminModalOpen] = useState(false);
  const { createBusinessInvitation, loading: creatingBusinessInvitation } = useCreateBusinessInvitation(businessId);
  const [createBusinessInvitationInvitationError, setCreateBusinessInvitationError] = useState<LensSDKException>();
  const [userTableActiveTabIndex, setUserTableActiveTabIndex] = useState<number | undefined>(undefined);
  const { deleteBusinessUser, error: deleteBusinessUserError } = useDeleteBusinessUser();
  const { changeBusinessUserRole, error: changeBusinessUserRoleError } = useChangeBusinessUserRole();
  const { deleteBusinessInvitation, error: deleteBusinessInvitationError } = useDeleteBusinessInvitation();
  const [error, setError] = useState<Error | null>(null);
  const openInviteAdministrationModal = useCallback(() => {
    setInviteAdminModalOpen(true);
  }, []);
  const closeInviteAdminModal = useCallback(() => {
    setInviteAdminModalOpen(false);
    setCreateBusinessInvitationError(undefined);
  }, []);

  const reload = useCallback(async () => Promise.all([
    reloadBusinessInvitations(),
    reloadBusinessUsers()]), [
    reloadBusinessInvitations,
    reloadBusinessUsers]);

  const onClickInvite = useCallback(async (emails: string) => {
    const emailArray = getUniqueEmailsArray(emails);

    setCreateBusinessInvitationError(undefined);

    for (const email of emailArray) {
      // eslint-disable-next-line no-await-in-loop
      const exception = await createBusinessInvitation(email, undefined, "Administrator");

      if (exception) {
        setCreateBusinessInvitationError(exception);
        trackError("Admin Invitation Failed");

        return;
      }
      track("Invited Admin to Business");
    }

    await reload();

    closeInviteAdminModal();

    // Set active tab to "Pending" to show pending admin invitations.
    if (userTableActiveTabIndex !== 1) {
      setUserTableActiveTabIndex(1);
    }
  }, [
    userTableActiveTabIndex,
    closeInviteAdminModal,
    createBusinessInvitation,
    track,
    trackError,
    reload
  ]);

  const handleDeleteBusinessUser = useCallback(async (businessId: string, businessUserId: string, userId: string) => {
    await deleteBusinessUser(businessId, businessUserId);

    // if user is deleting himself from LBID, redirect to home page as this LBID is no longer accessible
    if (profile?.id === userId) {
      history.push("/home");
    }
  }, [deleteBusinessUser, profile?.id, history]);

  const handleRemoveUserFromBusiness = useCallback(async (userId: string, businessUserId: string) => {
    if (!businessId) {
      return;
    }

    setError(null);

    if (activeBusinessAdministrators.length <= 1) {
      setError(new Error("Can't delete the last user in business"));

      return;
    }

    // if user has subscriptions - change role to Member
    if (getUserSubscriptions(businessSubscriptions, userId).length > 0) {
      await changeBusinessUserRole(businessId, businessUserId, "Member");
      await reload();
    } else {
      await handleDeleteBusinessUser(businessId, businessUserId, userId);
      await reload();
    }
  }, [businessId, businessSubscriptions, activeBusinessAdministrators.length,
    changeBusinessUserRole, handleDeleteBusinessUser, reload]);

  const handleRemoveBusinessInvitation = async (id: string) => {
    if (!businessId) {
      return;
    }

    await deleteBusinessInvitation(businessId, id);
    await reload();
  };

  const { businessParent } = useBusinessParent(businessId);

  return (
    <Layout className="bg-secondary" logout={logout} profile={profile} business={business}>
      {
          (error
          || deleteBusinessUserError
          || changeBusinessUserRoleError
          || deleteBusinessInvitationError) && (
          <Notification
            type="flash"
            level="error"
            message={
              error?.message
              || deleteBusinessUserError?.message
              || changeBusinessUserRoleError?.message
              || deleteBusinessInvitationError?.message
          }
          />
          )
      }
      <header>
        <h2 className={clsx("lds-h2-caps", styles.welcome)}>
          Account
        </h2>
        <p>
          Manage your Lens Business ID
          {" "}
          <strong>{`(${businessId})`}</strong>
          {" "}
          administrators and account settings in here.
        </p>
      </header>
      <section>
        <h3 className={styles.sectionTitle}>Administrators</h3>
        <Administrators
          businessAdministrators={activeBusinessAdministrators}
          businessAdminInvitations={businessAdminInvitations}
          selectedTableTabIndex={userTableActiveTabIndex}
          setSelectedTableTabIndex={setUserTableActiveTabIndex}
          onRemoveBusinessUser={handleRemoveUserFromBusiness}
          onRemoveBusinessInvitation={handleRemoveBusinessInvitation}
        />
        <ButtonBar type="grid" className={styles.buttonBar}>
          <TrackedButton
            label={inviteAdminLabel}
            onClick={() => {
              openInviteAdministrationModal();
            }}
            primary
          />
        </ButtonBar>
      </section>
      {isNotProduction ? (
        <section className="mt-14">
          <h3 className={styles.sectionTitle}>
            Lens Business ID
            {" "}
            {businessParent ? "Parent" : "Children"}
            {" "}
            Management
          </h3>
          {/* if the business has a parent, it won't/can't have children accounts */}
          {businessParent ? <Parent /> : <Children />}
        </section>
      ) : null}
      <InviteAdminModal
        open={inviteAdminModalOpen}
        inviting={creatingBusinessInvitation}
        inviteError={createBusinessInvitationInvitationError}
        onModalClose={closeInviteAdminModal}
        loadingBusinessUsers={loadingBusinessUsers}
        onClickInvite={onClickInvite}
      />
    </Layout>
  );
};

export default Account;
